import * as Sentry from "@sentry/react";

import { getPluginVersion } from "./envManager";

export type ReportLevel = "fatal" | "error" | "warning" | "log" | "info" | "debug";

export interface ReporterContext {
    page: string | null;
    userId: string | null;
    orgId: string | null;
    conversationId: string | null;
}

export interface ErrorReport {
    level?: ReportLevel;
    fingerprint?: string[];
    tags?: Record<string, string>;
    contexts?: Record<string, Record<string, unknown>>;
    extra?: Record<string, unknown>;
}

export type LogAttributeValue = string | number | boolean | string[] | number[] | boolean[];
export type LogAttributes = Record<string, LogAttributeValue>;

interface AddinEventContext extends ReporterContext {
    pluginVersion: string;
    url: string;
    userAgent: string;
    timestamp: string;
}

interface SentryCaptureContext {
    level: ReportLevel;
    fingerprint?: string[];
    tags?: Record<string, string>;
    contexts: Record<string, Record<string, unknown>>;
    extra?: Record<string, unknown>;
}

export const ADDIN_CONTEXT_KEY = "addin";
export const CONVERSATION_ID_TAG = "conversation_id";
export const LOG_CONVERSATION_ID_ATTRIBUTE = "addin.conversation_id";
export const LOG_PAGE_ATTRIBUTE = "addin.page";

const EMPTY_REPORTER_CONTEXT: ReporterContext = { page: null, userId: null, orgId: null, conversationId: null };

let reporterContext: ReporterContext = { ...EMPTY_REPORTER_CONTEXT };

export function setReporterContext(update: Partial<ReporterContext>): void {
    reporterContext = { ...reporterContext, ...update };
    if (update.conversationId !== undefined) Sentry.setTag(CONVERSATION_ID_TAG, update.conversationId ?? undefined);
}

export function reporterContextSnapshot(): ReporterContext {
    return { ...reporterContext };
}

export function resetReporterContext(): void {
    reporterContext = { ...EMPTY_REPORTER_CONTEXT };
}

function addinEventContext(): AddinEventContext {
    return {
        ...reporterContext,
        pluginVersion: getPluginVersion(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
    };
}

function captureContextOf(report: ErrorReport, defaultLevel: ReportLevel): SentryCaptureContext {
    const captureContext: SentryCaptureContext = {
        level: report.level ?? defaultLevel,
        contexts: { ...report.contexts, [ADDIN_CONTEXT_KEY]: { ...addinEventContext() } },
    };
    if (report.fingerprint) captureContext.fingerprint = report.fingerprint;
    if (report.tags) captureContext.tags = report.tags;
    if (report.extra) captureContext.extra = report.extra;
    return captureContext;
}

export function reportError(error: unknown, report: ErrorReport = {}): void {
    const captureContext = captureContextOf(report, "error");
    Sentry.captureException(error, captureContext);
}

export function reportMessage(message: string, report: ErrorReport = {}): void {
    const captureContext = captureContextOf(report, "info");
    Sentry.captureMessage(message, captureContext);
}

function logAttributesOf(attributes: LogAttributes): LogAttributes {
    const attributesWithContext: LogAttributes = { ...attributes };
    if (reporterContext.conversationId !== null) attributesWithContext[LOG_CONVERSATION_ID_ATTRIBUTE] = reporterContext.conversationId;
    if (reporterContext.page !== null) attributesWithContext[LOG_PAGE_ATTRIBUTE] = reporterContext.page;
    return attributesWithContext;
}

export function logInfo(message: string, attributes: LogAttributes = {}): void {
    Sentry.logger.info(message, logAttributesOf(attributes));
}

export function logWarning(message: string, attributes: LogAttributes = {}): void {
    Sentry.logger.warn(message, logAttributesOf(attributes));
}
